/**
 * Parent Bridge Storage Adapter
 * 
 * Uses iframe-resizer's postMessage API to store data in parent window.
 * This enables persistent storage in iframe contexts where localStorage is blocked.
 * 
 * Requires parent page to have storage bridge script installed.
 * 
 * Pros:
 * - Works in third-party iframes
 * - Persistent (parent's localStorage)
 * - Origin validation built-in (iframe-resizer)
 * 
 * Cons:
 * - Requires parent cooperation
 * - Async (postMessage round-trip)
 * - Parent page controls the data
 */

import type { StorageAdapter, StorageRequest, StorageResponse } from '../types';
import { MESSAGE_TYPES, ADAPTER_CONFIG } from '../constants';
import { hasIframeResizer, isInIframe } from '../utils/detector';

/**
 * Generate unique message ID
 */
function generateMessageId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Parent bridge storage adapter
 */
class ParentBridgeAdapter implements StorageAdapter {
  readonly name = 'parent';

  private pendingRequests = new Map<
    string,
    {
      resolve: (value: StorageResponse) => void;
      reject: (error: Error) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  >();

  constructor() {
    this.setupMessageHandler();
  }

  /**
   * Setup message handler for responses from parent
   * 
   * NOTE: This is safe from race conditions because:
   * 1. ParentBridgeAdapter is a singleton (only one instance)
   * 2. Constructor is called once during browserAdapter initialization
   * 3. Message handler is set up before any storage operations
   */
  private setupMessageHandler(): void {
    if (!hasIframeResizer()) return;

    // Check if handler already set up (defensive check, shouldn't happen with singleton)
    if (window.iFrameResizer?.onMessage && 
        window.iFrameResizer.onMessage.toString().includes('isStorageResponse')) {
      if (import.meta.env.DEV) {
        console.warn('[ParentBridgeAdapter] Message handler already set up, skipping');
      }
      return;
    }

    const originalOnMessage = window.iFrameResizer?.onMessage;

    window.iFrameResizer = {
      ...window.iFrameResizer,
      onMessage: (message: unknown) => {
        // Handle storage responses
        if (this.isStorageResponse(message)) {
          this.handleResponse(message);
        }

        // Pass to original handler
        if (originalOnMessage) {
          originalOnMessage(message);
        }
      },
    };
  }

  /**
   * Type guard for storage response
   */
  private isStorageResponse(message: unknown): message is StorageResponse {
    return (
      typeof message === 'object' &&
      message !== null &&
      'type' in message &&
      message.type === MESSAGE_TYPES.STORAGE_RESPONSE &&
      'id' in message
    );
  }

  /**
   * Handle response from parent
   */
  private handleResponse(response: StorageResponse): void {
    const pending = this.pendingRequests.get(response.id);
    if (!pending) return;

    clearTimeout(pending.timeout);
    this.pendingRequests.delete(response.id);

    if (response.success) {
      pending.resolve(response);
    } else {
      pending.reject(new Error(response.error || 'Parent bridge request failed'));
    }
  }

  /**
   * Send request to parent and wait for response
   */
  private async sendRequest(request: StorageRequest): Promise<StorageResponse> {
    const parentIFrame = window.parentIFrame;
    
    if (!parentIFrame) {
      throw new Error('iframe-resizer not available');
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(request.id);
        reject(new Error('Parent bridge timeout'));
      }, ADAPTER_CONFIG.parentBridgeTimeout);

      this.pendingRequests.set(request.id, {
        resolve,
        reject,
        timeout,
      });

      try {
        parentIFrame.sendMessage(request);
      } catch (error) {
        clearTimeout(timeout);
        this.pendingRequests.delete(request.id);
        reject(error);
      }
    });
  }

  async get(key: string): Promise<string | null> {
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_GET,
        id: generateMessageId(),
        key,
      };

      const response = await this.sendRequest(request);
      return response.value ?? null;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[ParentBridge] Failed to get:', key, error);
      }
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_SET,
        id: generateMessageId(),
        key,
        value,
      };

      await this.sendRequest(request);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[ParentBridge] Failed to set:', key, error);
      }
      throw new Error(`Failed to save via parent bridge: ${error}`);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_REMOVE,
        id: generateMessageId(),
        key,
      };

      await this.sendRequest(request);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[ParentBridge] Failed to remove:', key, error);
      }
    }
  }

  async clear(): Promise<void> {
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_CLEAR,
        id: generateMessageId(),
      };

      await this.sendRequest(request);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[ParentBridge] Failed to clear:', error);
      }
      throw new Error(`Failed to clear via parent bridge: ${error}`);
    }
  }

  async keys(): Promise<string[]> {
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_KEYS,
        id: generateMessageId(),
      };

      const response = await this.sendRequest(request);
      return response.keys ?? [];
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[ParentBridge] Failed to get keys:', error);
      }
      return [];
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!isInIframe()) return false;
    if (!hasIframeResizer()) return false;

    // Test if parent responds
    try {
      const request: StorageRequest = {
        type: MESSAGE_TYPES.STORAGE_GET,
        id: generateMessageId(),
        key: '__test__',
      };

      await this.sendRequest(request);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Singleton instance
 */
export const parentBridgeAdapter = new ParentBridgeAdapter();
